import { useCallback, useMemo } from 'react';
import { useDisclosure } from '@shared/hooks';
import { useToast } from '@retail/hooks';
import { useAppTFunction } from '@retail/app/i18n';
import { generateXLSX, XLSXRow } from '@retail/products/utils';
import { CentralAssortmentItem, ExportCentralAssortmentItems } from '@retail/my-assortment/types';
import { centralAssortmentValueMap } from './centralAssortmentValueMap';
import {
  centralAssortmentColumnHeaders,
  CentralAssortmentExportColumn,
} from './centralAssortmentColumnHeaders';
import dayjs from 'dayjs';

interface Props {
  items: CentralAssortmentItem[];
  fileName?: string;
  includeSubAssortments?: boolean;
}

const subAssortmentColumns: CentralAssortmentExportColumn[] = ['assortmentTag', 'tagFrom', 'tagTo'];

export const useExportCentralAssortmentToExcel = ({
  items,
  fileName,
  includeSubAssortments = true,
}: Props) => {
  const t = useAppTFunction();
  const toast = useToast();
  const { isOpen: isLoading, onOpen: onLoadingStart, onClose: onLoadingEnd } = useDisclosure(false);

  const subAssortmentItems: ExportCentralAssortmentItems[] = useMemo(
    () =>
      items.flatMap((item) => {
        if (includeSubAssortments && item.assortmentTags && item.assortmentTags.length > 0) {
          return item.assortmentTags.map((tag) => {
            return {
              ...item,
              assortmentTag: tag.tagName,
              tagFrom: tag.validFrom,
              tagTo: tag.validTo,
            };
          });
        } else {
          return {
            ...item,
            assortmentTag: undefined,
            tagFrom: undefined,
            tagTo: undefined,
          } as ExportCentralAssortmentItems;
        }
      }),
    [includeSubAssortments, items]
  );

  const exportToExcel = useCallback(async () => {
    onLoadingStart();
    const columns = (
      Object.keys(centralAssortmentColumnHeaders(t)) as CentralAssortmentExportColumn[]
    ).filter((column) => includeSubAssortments || !subAssortmentColumns.includes(column));

    const headerRow: XLSXRow = columns.map((column) => centralAssortmentColumnHeaders(t)[column]);

    const dataRows: XLSXRow[] = subAssortmentItems.map((item) =>
      columns.map((column) => centralAssortmentValueMap[column](item, t))
    );

    await generateXLSX({
      rows: [headerRow, ...dataRows],
      fileName: fileName ?? `centralAssortment ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`,
    });

    toast.success(t('export.messages.fileDownloaded'));

    onLoadingEnd();
  }, [fileName, includeSubAssortments, subAssortmentItems, onLoadingEnd, onLoadingStart, t, toast]);

  return useMemo(
    () => ({
      isLoading,
      exportToExcel,
    }),
    [exportToExcel, isLoading]
  );
};
